import { Box } from "@mui/material";

import { format } from "date-fns";
import type { CardTransactionFilters } from "Components/CardTransactions/utils/cardTransactionFilterUtils";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Shared.Filters;
const StatusResources = Resources.Components.CardTransactions.Status;

type FilterItem = {
  key: string;
  label: string;
  value?: string;
};

type Props = {
  filters: Partial<CardTransactionFilters>;
  isVisible?: boolean;
};

const formatDateRange = (
  dateFrom?: Date,
  dateTo?: Date,
): string | undefined => {
  if (!dateFrom && !dateTo) {
    return undefined;
  }

  const fromStr = dateFrom ? format(dateFrom, "d.M.yyyy") : "";
  const toStr = dateTo ? format(dateTo, "d.M.yyyy") : "";

  if (fromStr && toStr) {
    return `${fromStr} - ${toStr}`;
  }

  return fromStr || toStr;
};

export const CardTransactionFilterChips = ({
  filters: {
    timePeriod,
    dateFrom,
    dateTo,
    payPoint,
    employee,
    status,
    isRefundOrCancellation,
  },
  isVisible = false,
}: Props) => {
  const { t } = useResource();

  const getTimePeriodValue = (): string | undefined => {
    // Undefined/unset period ("Neomezeno") - don't show chip
    if (!timePeriod) {
      return undefined;
    }

    // Custom period - show actual dates
    if (timePeriod === "custom") {
      return (
        formatDateRange(dateFrom, dateTo) ??
        t(RESOURCES.TimePeriodLabels.custom)
      );
    }

    // All other periods (day, week, month) - show label
    return t(RESOURCES.TimePeriodLabels[timePeriod]);
  };

  const items: Array<FilterItem> = [
    {
      key: "timePeriod",
      label: t(RESOURCES.TimePeriod),
      value: getTimePeriodValue(),
    },
    {
      key: "payPoint",
      label: t(RESOURCES.PayPoint),
      value: payPoint?.label,
    },
    {
      key: "employee",
      label: t(RESOURCES.Employee),
      value: employee?.label,
    },
    {
      key: "status",
      label: t(RESOURCES.Status),
      value: status ? t(StatusResources[status]) : undefined,
    },
    {
      key: "refundOrCancellation",
      label: t(RESOURCES.RefundOrCancellation),
      value: isRefundOrCancellation
        ? t(RESOURCES.ShowOnlyRefundsAndCancellations)
        : undefined,
    },
  ];

  const activeFilters = items.filter(({ value }) => !!value);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateRows: isVisible ? "1fr" : "0fr",
        transition: "grid-template-rows 300ms ease-in",
      }}
    >
      <Box sx={{ overflow: "hidden" }}>
        <Box component="dl" display="flex" flexWrap="wrap" gap={2} m={0}>
          {activeFilters.map(({ key, label, value }) => (
            <Box key={key} display="flex">
              <Box
                component="dt"
                sx={{ mr: 1, fontWeight: 600, color: "primary.main" }}
              >
                {label}:
              </Box>
              <Box component="dd" m={0} color="primary.main">
                {value}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
